import * as React from 'react';

import { ContactForm } from '../../contact-form';
import { useSanityGeneralSettings } from '../../hooks/use-sanity-general-settings';
import { TColourScheme } from '../../types';
import { classNames } from '../../utils/classnames';
import { getIcon } from '../../utils/get-icon';
import { BG_COLOUR_MAP } from '../../utils/object-dictionaries';

interface ContactSectionProps {
  contactSection?: {
    colourScheme: TColourScheme;
  };
}

function ContactSection({ contactSection }: ContactSectionProps): JSX.Element {
  return (
    <div
      className={classNames(
        'bg-dark',
        contactSection?.colourScheme &&
          BG_COLOUR_MAP[contactSection.colourScheme]
      )}
    >
      <div className="w-full px-4 py-12 mx-auto max-w-prose lg:py-24 lg:max-w-screen-2xl sm:px-6 lg:px-8">
        <div className="grid gap-8 lg:grid-cols-2">
          <ContactInfo />
          <ContactForm />
        </div>
      </div>
    </div>
  );
}

function ContactInfo(): JSX.Element {
  const settings = useSanityGeneralSettings();
  return (
    <div className="text-light">
      <h2 className="text-2xl font-bold text-primary">Contact us</h2>
      <dl className="mt-6 space-y-4">
        <div>
          <dt className="inline font-bold">Phone: </dt>
          <dd className="inline">
            <a
              href={`tel:${settings.phoneNumber}`}
              className="hover:underline focus:underline"
            >
              {settings.phoneNumber}
            </a>
          </dd>
        </div>
        <div>
          <dt className="inline font-bold">Email: </dt>
          <dd className="inline">
            <a
              href={`mailto:${settings.email}`}
              className="hover:underline focus:underline"
            >
              {settings.email}
            </a>
          </dd>
        </div>
        <div>
          <dt className="inline font-bold">Address: </dt>
          <dd className="inline">
            <a
              href={settings.address.googleMaps.link}
              className="hover:underline focus:underline"
            >
              {settings.address.streetAddress}, {settings.address.suburb}
            </a>
          </dd>
        </div>
        <div>
          <dt className="inline font-bold">Business Hours: </dt>
          <dd className="inline">
            <ul className="inline">
              {settings.hours.map(({ days, hours, id }) => (
                <li key={id} className="inline">
                  {days} – {hours}
                </li>
              ))}
            </ul>
          </dd>
        </div>
        <div className="inline-flex items-center space-x-2">
          <dt className="inline font-bold">Follow Us: </dt>
          <dd className="inline">
            {settings.socialLinks.map((socialLink) => (
              <a
                key={socialLink.id}
                href={socialLink.url}
                className="inline-block hover:underline focus:underline"
              >
                <span className="sr-only">{socialLink.icon}</span>
                {getIcon({
                  socialNetwork: socialLink.icon,
                  className: 'w-6 h-6',
                })}
              </a>
            ))}
          </dd>
        </div>
      </dl>
    </div>
  );
}

export { ContactSection };
